/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { MenuWidget, Modal } from '../../../UiKit';
import {
  cookieToken,
  getCurrentUserId,
  hasCookieToken,
  parseJwt,
} from '../../../utils/CookieToken';
import './UserWidget.scss';
import PlaygroundMode from './WidgetComps/Playground';
import SwitchRole from './WidgetComps/SwitchRole';
import { GenerateQr } from './WidgetComps/GenerateQr';
import { Logout, handleSignout } from './WidgetComps/Logout';
import history from '../../../history';
import getStore from '../../../store/store';
import AdminDashboardOption from './WidgetComps/AdminDashboard';
import SwitchUI from './WidgetComps/SwitchUI';
import LifelineDashboardOption from './WidgetComps/LifelineDashboard';
import { useEffect } from 'react';

const getInitials = (identifier) =>
  String(identifier || '')
    .trim()
    .split('@')[0]
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const UserWidget = ({
  openUserWidget: isOpenWidget,
  setOpenUserWidget: setOpenWidget,
  // Opt-in only — Policy Center's nav asks for the avatar-circle + "ID: ..."
  // subtitle treatment; every other caller (Lifeline, Admin Dashboard,
  // basepage) keeps the original plain name + chevron trigger untouched.
  showProfileCard = false,
}) => {
  const [showPopUp, setShowPopUp] = useState();
  const [popUpDetails, setPopDetails] = useState({});

  const pathSegments = window.location.pathname.split('/');
  const currentSection = pathSegments[1];

  let parsedData = parseJwt(cookieToken);
  const userName = parsedData.name;
  const userId = getCurrentUserId() || '';
  const store = getStore(history);
  const state = store.getState();
  const { authState = {} } = state;

  const { isAuthenticated, userDetails, isRevampEnabled } = useSelector(
    (state) => ({
      isAuthenticated: state.authState?.isAuthenticated,
      userDetails: state.authState?.userDetails,
      isRevampEnabled: state.ordersState?.isRevampEnabled,
    }),
  );

  const initials = getInitials(userName || userDetails?.email_id);

  const closePopUp = useCallback(() => {
    setShowPopUp(false);
    setPopDetails({});
  }, []);

  // store username
  useEffect(() => {
    localStorage.setItem('lastLoggedInUser', userName || '');
  }, [userName]);

  // store email
  useEffect(() => {
    localStorage.setItem('lastLoggedInEmail', userDetails?.email_id || '');
  }, [userDetails?.email_id]);

  if (!isAuthenticated && !hasCookieToken) return null;

  if (!cookieToken) {
    handleSignout();
    return null;
  }

  let userImage = null;
  try {
    userImage = JSON.parse(localStorage.getItem('userImage'));
  } catch (e) {
    userImage = null;
  }

  const avatarUrl = typeof userImage === 'string' && userImage ? userImage : '';

  return (
    <div>
      {/* The profile-card variant renders the avatar *inside* MenuWidget's
          head (photo when there is one, initials otherwise) so the trigger
          shows a single circle. Every other caller keeps this standalone
          image exactly as it was. */}
      {userImage && !showProfileCard ? (
        <img
          data-dd-privacy='hidden'
          referrerPolicy='no-referrer'
          src={userImage}
          className='tw-w-10 tw-h-10 tw-rounded-full tw-mr-3'
          alt='user-google-image'
        />
      ) : null}
      <MenuWidget
        isOpenWidget={isOpenWidget}
        setOpenWidget={setOpenWidget}
        title={userName ? userName : userDetails?.email_id || 'user'}
        subtitle={showProfileCard && userId ? `ID: ${userId}` : ''}
        picture={showProfileCard ? avatarUrl || undefined : undefined}
        initials={showProfileCard && !avatarUrl ? initials : ''}
        headMaxWidth={showProfileCard ? '22rem' : undefined}
        distanceFromTop='4rem'
        menuWidgetWidth='20rem'
        menuText={
          <div className='tw-flex-col tw-gap-1 tw-w-full'>
            {/* Playground option */}
            <PlaygroundMode />

            {/* switch UI version option */}
            {isRevampEnabled && <SwitchUI />}

            {/* Admin Dashboard option */}
            {currentSection !== 'admin-dashboard' && <AdminDashboardOption />}

            {/* Lifeline Dashboard option */}
            {currentSection !== 'lifeline' && <LifelineDashboardOption />}

            {authState?.roleDetails && !authState?.roleDetails?.isError ? (
              <SwitchRole
                closePopUp={closePopUp}
                setShowPopUp={setShowPopUp}
                setPopDetails={setPopDetails}
              />
            ) : null}
            <GenerateQr
              closePopUp={closePopUp}
              setShowPopUp={setShowPopUp}
              setPopDetails={setPopDetails}
            />
            <Logout />
          </div>
        }
      />
      {showPopUp && (
        <Modal
          isVisible
          type={popUpDetails.type}
          title={popUpDetails.title}
          paddingTop={0}
          handleClose={popUpDetails.handlClose}
          minHeight={popUpDetails.minHeight}
          maxHeight={popUpDetails.maxHeight}
          component={popUpDetails.component}
          showScrollBar={popUpDetails.showScrollBar}
        />
      )}
    </div>
  );
};

export default UserWidget;
